import { useMemo, memo } from 'react'
import * as React from 'react'
import QRCode from 'qrcode-generator'

type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H'
type Logo = string | React.ReactElement<React.SVGProps<SVGSVGElement>>

interface Props {
  value: string
  size?: number
  logo?: Logo
  dotColor?: string
  backgroundColor?: string
  logoSizeRatio?: number
  ecLevel?: ErrorCorrectionLevel
}

const FINDER_SIZE = 7

const FINDER_ORIGINS: Array<(moduleCount: number) => [number, number]> = [
  () => [0, 0],
  (mc) => [0, mc - FINDER_SIZE],
  (mc) => [mc - FINDER_SIZE, 0],
]

function generateQR(value: string, ecLevel: ErrorCorrectionLevel) {
  const qr = QRCode(0, ecLevel)
  qr.addData(value)
  qr.make()
  return qr
}

function isInFinderPattern(row: number, col: number, moduleCount: number): boolean {
  const s = FINDER_SIZE
  const inTopLeft = row < s && col < s
  const inTopRight = row < s && col >= moduleCount - s
  const inBottomLeft = row >= moduleCount - s && col < s
  return inTopLeft || inTopRight || inBottomLeft
}

function isInLogoZone(row: number, col: number, center: number, radiusSq: number): boolean {
  const dx = col - center
  const dy = row - center
  return (dx * dx + dy * dy) <= radiusSq
}

function renderLogo(logo: Logo, size: number, logoPixelSize: number) {
  const logoPosition = size / 2 - logoPixelSize / 2

  if (typeof logo === 'string') {
    return (
      <image
        href={logo}
        x={logoPosition}
        y={logoPosition}
        width={logoPixelSize}
        height={logoPixelSize}
        preserveAspectRatio="xMidYMid meet"
      />
    )
  }

  return React.cloneElement(logo, {
    x: logoPosition,
    y: logoPosition,
    width: logoPixelSize,
    height: logoPixelSize,
    preserveAspectRatio: 'xMidYMid meet',
  })
}

function ExperiencesQRCode({
  value,
  size = 360,
  logo,
  dotColor = '#000000',
  backgroundColor = '#ffffff',
  logoSizeRatio = 0.32,
  ecLevel = 'M',
}: Props) {
  const qr = useMemo(() => generateQR(value, ecLevel), [value, ecLevel])

  const moduleCount = qr.getModuleCount()
  const cellSize = size / moduleCount
  const dotRadius = cellSize * 0.4
  const center = (moduleCount - 1) / 2

  const MIN_MODULES = 21
  const SCALE_MODULES = 37
  const MIN_LOGO_RATIO = 0.22
  const t = Math.min(1, Math.max(0, (moduleCount - MIN_MODULES) / (SCALE_MODULES - MIN_MODULES)))
  const effectiveLogoRatio = MIN_LOGO_RATIO + t * (logoSizeRatio - MIN_LOGO_RATIO)

  const logoRadiusModules = (moduleCount * effectiveLogoRatio) / 2 + 0.5
  const logoRadiusSq = logoRadiusModules * logoRadiusModules

  const dots = useMemo(() => {
    const elements: Array<React.ReactElement> = []

    for (let r = 0; r < moduleCount; r++) {
      for (let c = 0; c < moduleCount; c++) {
        if (!qr.isDark(r, c)) continue
        if (isInFinderPattern(r, c, moduleCount)) continue
        if (logo && isInLogoZone(r, c, center, logoRadiusSq)) continue

        elements.push(
          <circle
            key={`${r}-${c}`}
            cx={(c + 0.5) * cellSize}
            cy={(r + 0.5) * cellSize}
            r={dotRadius}
            fill={dotColor}
          />,
        )
      }
    }

    return elements
  }, [qr, moduleCount, cellSize, dotRadius, dotColor, logo, center, logoRadiusSq])

  const finderPatterns = useMemo(() => {
    const outerSize = FINDER_SIZE * cellSize
    const outerRx = outerSize * 0.36
    const midOffset = cellSize
    const midSize = 5 * cellSize
    const midRx = midSize * 0.28
    const innerOffset = 2 * cellSize
    const innerSize = 3 * cellSize
    const innerRx = innerSize * 0.3

    return FINDER_ORIGINS.map((getOrigin, i) => {
      const [originRow, originCol] = getOrigin(moduleCount)
      const x = originCol * cellSize
      const y = originRow * cellSize

      return (
        <g key={`finder-${i}`}>
          <rect
            x={x}
            y={y}
            width={outerSize}
            height={outerSize}
            rx={outerRx}
            fill={dotColor}
          />
          <rect
            x={x + midOffset}
            y={y + midOffset}
            width={midSize}
            height={midSize}
            rx={midRx}
            fill={backgroundColor}
          />
          <rect
            x={x + innerOffset}
            y={y + innerOffset}
            width={innerSize}
            height={innerSize}
            rx={innerRx}
            fill={dotColor}
          />
        </g>
      )
    })
  }, [moduleCount, cellSize, dotColor, backgroundColor])

  const logoPixelSize = size * effectiveLogoRatio
  const logoClearRadius = logoPixelSize * 0.58

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ background: backgroundColor }}
    >
      {dots}
      {finderPatterns}
      {logo && (
        <g>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={logoClearRadius}
            fill={backgroundColor}
          />
          {renderLogo(logo, size, logoPixelSize)}
        </g>
      )}
    </svg>
  )
}

export default memo(ExperiencesQRCode)
